"use client"

import { useEffect, useState } from 'react'
import { 
  AlertCircle, 
  Info, 
  AlertTriangle, 
  XCircle, 
  X, 
  RefreshCw,
  WifiOff,
  Clock,
  ShieldAlert
} from 'lucide-react'
import { Button } from '@repo/ui'
import type { TherapistAppError, ErrorSeverity } from '@/lib/utils/error-migration-helper'

interface ErrorDisplayProps {
  error: TherapistAppError
  onDismiss?: () => void
  className?: string
}

const iconMap: Record<ErrorSeverity, typeof AlertCircle> = {
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
  critical: ShieldAlert
}

const colorMap: Record<ErrorSeverity, {
  bg: string
  border: string
  icon: string
  text: string
  button: string
}> = {
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    text: 'text-blue-800',
    button: 'text-blue-600 hover:text-blue-700 hover:bg-blue-100'
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-600',
    text: 'text-yellow-800',
    button: 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100'
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    text: 'text-red-800',
    button: 'text-red-600 hover:text-red-700 hover:bg-red-100'
  },
  critical: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    icon: 'text-purple-600',
    text: 'text-purple-800',
    button: 'text-purple-600 hover:text-purple-700 hover:bg-purple-100'
  }
}

export function ErrorDisplay({ error, onDismiss, className = '' }: ErrorDisplayProps) {
  const [isVisible, setIsVisible] = useState(true)
  const severity = error.severity || 'error'
  const Icon = iconMap[severity]
  const colors = colorMap[severity]
  
  // Auto-dismiss info messages after 5 seconds
  useEffect(() => {
    if (error.severity === 'info' && onDismiss) {
      const timer = setTimeout(() => {
        setIsVisible(false)
        setTimeout(onDismiss, 300) // Wait for animation
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error.severity, onDismiss])

  const handleDismiss = () => {
    setIsVisible(false)
    setTimeout(() => onDismiss?.(), 300) // Wait for animation
  }

  // Special icons for specific error types
  const getIcon = () => {
    if (error.category === 'network' && error.message.includes('connection')) {
      return WifiOff
    }
    if (error.message.includes('timed out')) {
      return Clock
    }
    return Icon
  }

  const SpecialIcon = getIcon()

  return (
    <>
      {isVisible && (
        <div
          className={`rounded-lg ${colors.bg} ${colors.border} border p-4 ${className} transition-all duration-300`}
        >
          <div className="flex">
            <div className="flex-shrink-0">
              <SpecialIcon className={`h-5 w-5 ${colors.icon}`} aria-hidden="true" />
            </div>
            
            <div className="ml-3 flex-1">
              <h3 className={`text-sm font-medium ${colors.text}`}>
                {error.message}
              </h3>
              
              {error.details && (
                <p className={`mt-1 text-sm ${colors.text} opacity-90`}>
                  {error.details}
                </p>
              )}
              
              {error.action && (
                <div className="mt-3">
                  {error.action.href ? (
                    <a
                      href={error.action.href}
                      className={`text-sm font-medium ${colors.button} inline-flex items-center px-3 py-1.5 rounded-md transition-colors`}
                    >
                      {error.action.label}
                      <RefreshCw className="ml-2 h-3 w-3" />
                    </a>
                  ) : (
                    <button
                      onClick={error.action.onClick}
                      className={`text-sm font-medium ${colors.button} inline-flex items-center px-3 py-1.5 rounded-md transition-colors`}
                    >
                      {error.action.label}
                      <RefreshCw className="ml-2 h-3 w-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {onDismiss && (
              <div className="ml-auto pl-3">
                <button
                  onClick={handleDismiss}
                  className={`inline-flex rounded-md p-1.5 ${colors.button} focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors`}
                >
                  <span className="sr-only">Dismiss</span>
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

// Toast variant for temporary notifications
export function ErrorToast({ error, onDismiss }: ErrorDisplayProps) {
  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-right">
      <ErrorDisplay error={error} onDismiss={onDismiss} />
    </div>
  )
}